import { Injectable, Component, ViewChild, ElementRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CollageSetService } from '../../services/collage-set.service';

@Injectable({ providedIn: 'root' })
export class PricingDetailDialogService {
  private _open$ = new BehaviorSubject<any>(null);
  open$ = this._open$.asObservable();

  open(data: { frameDetail: any[]; collageLayout: any; productType: any; product?: any }) {
    this._open$.next(data);
  }

  close() {
    this._open$.next(null);
  }
}

@Component({
  selector: 'app-pricing-detail-dialog',
  templateUrl: './pricing-detail-dialog.component.html'
})
export class PricingDetailDialogComponent {
  @ViewChild('tabScroll') tabScrollRef!: ElementRef;

  isOpen = false;
  isLoading = false;
  frameDetail: any[] = [];
  collageLayout: any = null;
  productType: any = null;
  product: any = null;
  activeTab = 0;

  // Pricing from API
  pricingDetails: any = null;

  constructor(
    public dialogService: PricingDetailDialogService,
    private collageService: CollageSetService
  ) {
    this.dialogService.open$.subscribe(data => {
      if (data) {
        this.isOpen = true;
        this.frameDetail = data.frameDetail || [];
        this.collageLayout = data.collageLayout || {};
        this.productType = data.productType || {};
        this.product = data.product || {};
        this.activeTab = 0;
        this.pricingDetails = null;
        this.loadPricing();
      } else {
        this.isOpen = false;
      }
    });
  }

  private loadPricing() {
    this.isLoading = true;
    const ptId = this.productType?.id || 5;
    const clId = this.collageLayout?.id || 0;

    this.collageService.getArtSetTotal(this.frameDetail, ptId, clId).subscribe({
      next: (res: any) => {
        const pd = res?.priceDetail || res?.data?.priceDetail || res?.data || {};
        // If API returns valid data use it, otherwise fallback
        if (pd.amount_payable > 0 || pd.total > 0) {
          this.pricingDetails = pd;
        } else {
          this.pricingDetails = this.calculateLocalPricing();
        }
        if (res?.productType) this.productType = res.productType;
        if (res?.product) this.product = res.product;
        this.isLoading = false;
      },
      error: () => {
        this.pricingDetails = this.calculateLocalPricing();
        this.isLoading = false;
      }
    });
  }

  private calculateLocalPricing(): any {
    // Use collage layout final_price as base
    let total = this.collageLayout?.final_price || 0;
    if (total === 0) {
      this.frameDetail.forEach((f: any) => {
        total += f.priceDetail?.total || f.priceDetail?.amount_payable || f.price || 0;
      });
    }
    const discountRate = 15;
    const discount = Math.round(total * discountRate / 100 * 100) / 100;
    const afterDiscount = total - discount;
    const taxRate = 18;
    const tax = Math.round(afterDiscount * taxRate / 100 * 100) / 100;
    const amountPayable = Math.round((afterDiscount + tax) * 100) / 100;
    return {
      total: Math.round(total * 100) / 100,
      discount_rate: discountRate,
      discount_with_tax: discount,
      tax_rate: taxRate,
      total_tax: tax,
      amount_payable: amountPayable,
    };
  }

  close() {
    this.dialogService.close();
  }

  scrollTabs(dir: number) {
    if (this.tabScrollRef?.nativeElement) {
      this.tabScrollRef.nativeElement.scrollBy({ left: dir * 150, behavior: 'smooth' });
    }
  }

  // Product detail helpers
  getImageId(frame: any): string {
    if (frame.image_id) return frame.image_id;
    if (frame.backgroundImage) {
      const parts = frame.backgroundImage.split('/');
      const filename = parts[parts.length - 1]?.split('.')[0] || '';
      return filename || 'N/A';
    }
    return 'N/A';
  }

  getPrintSize(frame: any): string {
    return `${frame.width || 0}X${frame.height || 0}`;
  }

  getFrameSize(frame: any): string {
    const inch = 8;
    return frame.frameBorderWidth ? `${(frame.frameBorderWidth / inch).toFixed(2)}"` : 'N/A';
  }

  getFinishedSize(frame: any): string {
    const inch = 8;
    const mounts = (frame.mount_size_1 || 0) + (frame.mount_size_2 || 0) + (frame.mount_size_3 || 0);
    const border = frame.frameBorderWidth || 0;
    const extra = ((mounts + border) / inch) * 2;
    return `${((frame.width || 0) + extra).toFixed(1)}X${((frame.height || 0) + extra).toFixed(1)}`;
  }

  getMountName(frame: any, index: number): string {
    const key = 'mount' + index + '_name';
    if (frame[key]) return frame[key];
    const bgKey = 'mount' + index + 'background';
    if (frame[bgKey] && frame[bgKey] !== 'transparent' && frame[bgKey] !== 'none') {
      // Try to extract name from URL
      const url = frame[bgKey] || '';
      const match = url.match(/mount_([^.]+)/);
      return match ? match[1].replace(/_/g, ' ') : 'Custom';
    }
    return '';
  }

  getMountSize(frame: any, index: number): number {
    const key = 'mount_size_' + index;
    return (frame[key] || 0) / 8;
  }

  isFrame(): boolean {
    return [1, 2, 5, 8].includes(this.productType?.id || 5);
  }
}
